import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';

const ROLE_OPTIONS = ['admin', 'hr', 'it_admin', 'payroll_manager', 'reporting_manager'];

export default function TicketCategories() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);   // category being edited
  const [createOpen, setCreateOpen] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/tickets/categories');
        if (!cancelled) setRows(data || []);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [reload]);

  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? This is irreversible.`)) return;
    try {
      await api.delete(`/tickets/categories/${c.id}`);
      toast.success(`Deleted ${c.name}`);
      setReload(r => r + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/tickets/categories/${c.id}`, { active: !c.active });
      toast.success(`${c.name} ${c.active ? 'deactivated' : 'activated'}`);
      setReload(r => r + 1);
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="ticket-categories-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Settings</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Ticket Categories</h1>
          <p className="text-slate-500 mt-1">Configure which roles handle each kind of ticket — and the SLA each owes.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="rounded-full h-10 bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="create-category-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New category
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Category</th>
              <th className="text-left px-4 py-3 font-semibold">Responsible roles</th>
              <th className="text-left px-4 py-3 font-semibold">Default priority</th>
              <th className="text-left px-4 py-3 font-semibold">SLA</th>
              <th className="text-left px-4 py-3 font-semibold">Active</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`cat-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900 inline-flex items-center gap-1.5">
                    {c.name}
                    {c.is_system && <Shield className="w-3.5 h-3.5 text-slate-400" title="System category — cannot be deleted" />}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 max-w-md">{c.description}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(c.responsible_roles || []).map(r => (
                      <span key={r} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{r.replace('_',' ')}</span>
                    ))}
                    {(!c.responsible_roles || c.responsible_roles.length === 0) && <span className="text-xs text-rose-600">No roles assigned!</span>}
                  </div>
                </td>
                <td className="px-4 py-3">{c.default_priority}</td>
                <td className="px-4 py-3">{c.sla_hours}h</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(c)} data-testid={`toggle-active-${c.id}`}
                          className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${c.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                    {c.active ? 'active' : 'inactive'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(c)} className="text-slate-500 hover:text-[#3D4A8E] p-1.5" data-testid={`edit-cat-${c.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {!c.is_system && (
                    <button onClick={() => del(c)} className="text-slate-500 hover:text-rose-600 p-1.5" data-testid={`del-cat-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-12 text-sm">No categories yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <CategoryDialog key={`new-${createOpen}`} open={createOpen} onClose={() => setCreateOpen(false)}
                      onSaved={() => { setCreateOpen(false); setReload(r => r + 1); }} />
      <CategoryDialog key={`edit-${editing?.id || 'none'}`} category={editing} open={!!editing} onClose={() => setEditing(null)}
                      onSaved={() => { setEditing(null); setReload(r => r + 1); }} />
    </div>
  );
}

function CategoryDialog({ category, open, onClose, onSaved }) {
  const isEdit = !!category;
  const [form, setForm] = useState({
    name: category?.name || '',
    description: category?.description || '',
    responsible_roles: category?.responsible_roles || [],
    default_priority: category?.default_priority || 'medium',
    sla_hours: category?.sla_hours || 24,
    active: category?.active ?? true,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.sla_hours <= 0) { toast.error('SLA must be > 0'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/tickets/categories/${category.id}`, form);
        toast.success('Category updated');
      } else {
        await api.post('/tickets/categories', form);
        toast.success('Category created');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const toggleRole = (r) => {
    setForm(f => ({
      ...f,
      responsible_roles: f.responsible_roles.includes(r)
        ? f.responsible_roles.filter(x => x !== r)
        : [...f.responsible_roles, r],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="category-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{isEdit ? `Edit ${category?.name}` : 'New ticket category'}</DialogTitle>
          <DialogDescription>Categories drive auto-routing and SLAs. Add a category for every kind of issue you want tracked separately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Name *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Travel Booking Issues" className="rounded-xl mt-1" data-testid="cat-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Description</label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" data-testid="cat-description" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Responsible role(s) *</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {ROLE_OPTIONS.map(r => (
                <button key={r} type="button" onClick={() => toggleRole(r)}
                        data-testid={`cat-role-${r}`}
                        className={`text-xs px-3 py-1 rounded-full border ${form.responsible_roles.includes(r) ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                  {r.replace('_',' ')}
                </button>
              ))}
            </div>
            {form.responsible_roles.length === 0 && <div className="text-[11px] text-rose-600 mt-1">Pick at least one role — tickets will auto-route to users holding it.</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Default priority</label>
              <Select value={form.default_priority} onValueChange={(v) => setForm({ ...form, default_priority: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="cat-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {['low','medium','high','critical'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">SLA (hours)</label>
              <Input type="number" min={1} value={form.sla_hours} onChange={(e) => setForm({ ...form, sla_hours: parseInt(e.target.value || '0', 10) })}
                     className="rounded-xl mt-1" data-testid="cat-sla" />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="cat-active" />
            Active (employees can pick this category)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy || form.responsible_roles.length === 0} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="confirm-save-category">
            {busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Create category')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
